import * as React from "react";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getPaginationRowModel,
    Row as RowInfo,
    useReactTable
} from "@tanstack/react-table";
import {Row, Col, Button, Table, Form} from "react-bootstrap";
import {inject, observer} from "mobx-react";
import {HistoryStore, History} from "Stores/historyStore";
import {Ticket} from "Stores/dashboardStore/tickets";
import * as _ from "lodash";
import {Fragment} from "react";
import { format } from "date-fns";

interface HistoryProps {
    historyStore?: HistoryStore,
    actionPlan: Ticket
}

interface HistoryState {
    expand?: {[index: number]: boolean}
    logs: History[]
    isLoading: boolean
}

@inject("historyStore")
@observer
export class TicketHistory extends React.Component<HistoryProps, HistoryState> {

    constructor(props) {
        super(props);
        this.state = {
            expand: undefined,
            logs: [],
            isLoading: true
        };
    }

    columns: ColumnDef<History>[] = [
        {
            header: "Date",
            accessorKey: "enflux_created",
            size: 200,
            cell: props => {
                const value = props.getValue() as string;
                if (!value) return "-";
                const date = new Date(value);
                return format(date, "do MMM yyyy, h:mmaaa");
            },
        },
        {
            header: "Change",
            accessorKey: "readable_change",
        },
        {
            header: "Changed By",
            accessorKey: "changer",
            size: 200,
            cell: props => {
                return props.row.original.changer ? props.row.original.changer : "Unknown";
            },
        },
        {
            id: "expander",
            header: () => null,
            size: 50,
            cell: props => {
                return (
                    <button className="table-action-button" onClick={ props.row.getToggleExpandedHandler() }>
                        <i className={`icon icon-${props.row.getIsExpanded() ? "compress" : "expand"}`} />
                    </button>);
            },
        },
    ];

    componentDidMount(){
        this.props.historyStore!.getAllHistoryForActionPlan(this.props.actionPlan.ruuid).then((logs: History[]) => {
            this.setState({
                logs: logs.slice(),
                isLoading: false
            });
        });
    }

    closeSubComponent = () => {
        this.setState({ expand: undefined});
    };

    mapAuditString(el: string, indexLabelGroup) {
        const labels = el.trim().split("with ['");
        let labelName = labels[0].trim();
        if (labelName[0].substring(0,1) === "[") {
            labelName = labelName.substring(2);
        } else {
            labelName = labelName.substring(1);
        }

        let labelsValues = "['" + labels[1];

        if (labelsValues.substring(labelsValues.length - 3, labelsValues.length) === "]\"]") {
            labelsValues = labelsValues.substring(0, labelsValues.length - 2);
        }
        const labelGroup = {
            name: labelName,
            labels: JSON.parse(labelsValues.split("'").join("\""))
        };

        return (
            <div key={indexLabelGroup + labelGroup.name}>
                {labelGroup.name}
                <ul>
                    {labelGroup.labels.map((el, id) => {
                        return (<li key={indexLabelGroup + labelGroup.name + el + id}>{el}</li>);
                    })}
                </ul>
            </div>
        );
    }

    getSubComponentByField(row: RowInfo<History>) {
        switch (row.original.field) {
            case "milestones":
            case "pdf_download":
            case "actionplan_create": {
                if (!row.original.readable_change) {
                    return null;
                }
                return (
                    <p>{row.original.readable_change}</p>
                );
            }
            case "assignee_ruuid":
            case "comment":
            case "committee_id":
            case "course_id":
            case "dashboard_interpretation":
            case "ddd":
            case "evaluation_date":
            case "objective":
            case "progress":
            case "status":
            case "strategic_plan_item_id":
            case "target":
            case "target_met":
            case "students": {
                if (!row.original.readable_change) {
                    return null;
                }
                const origin = row.original.readable_change.split("From::");
                if (origin.length === 1) {
                    return (<p>{row.original.readable_change}</p>);
                }
                const subtitle = origin[0];

                const changes = origin[1].split("To::");
                const from = changes[0];
                const to = changes[1];
                return (
                    <>
                        <h5>{subtitle.trim() + ":"}</h5>
                        <p>{from.trim()}</p>
                        <h5><strong>To</strong></h5>
                        <p>{to.trim()}</p>
                    </>
                );
            }
            case "collaborators": {
                if (!row.original.readable_change) {
                    return null;
                }
                const origin = row.original.readable_change.split("- Added");
                const subtitle = origin[0];
                const audit = origin[1].split("and Removed");
                let added = audit[0].trim();
                let removed = audit[1].trim();
                if (added === "") {
                    added = "None";
                }
                if (removed === "") {
                    removed = "None";
                }
                return (
                    <>
                        <h5>{subtitle.trim() + ":"}</h5>
                        <p>Added: {added}</p>
                        <p>Removed: {removed}</p>
                    </>
                );
            }
            case "labels": {
                if (!row.original.readable_change) {
                    return null;
                }
                const origin = row.original.readable_change.split("From::");
                if (origin.length !== 1) {
                    const subtitle = origin[0];
                    const audit = origin[1].split("To::");
                    const fromAudit = audit[0].split("\",");
                    const toAudit = audit[1].split("\",");
                    const fromAuditMapped = fromAudit.map(this.mapAuditString);
                    const toAuditMapped = toAudit.map(this.mapAuditString);
                    return (
                        <>
                            <h5>{subtitle.trim() + ":"}</h5>
                            {fromAuditMapped}
                            <h5><strong>To</strong></h5>
                            {toAuditMapped}
                        </>
                    );
                } else {
                    const addOrigin = row.original.readable_change.split(" - Added ");
                    const subtitle = addOrigin[0];
                    const audit = addOrigin[1].split(" and Removed ");
                    return (
                        <>
                            <h5>{subtitle.trim() + ":"}</h5>
                            <p>Added: {audit[0]}</p>
                            <p>Removed: {audit[1]}</p>
                        </>
                    );
                }

            }
            default:
                    return null;
        }
    }

    subcomponent = (row: RowInfo<History>) => {
        return(
            <div className={"observation-task-form"}>
                <h4>Changes:</h4>
                {this.getSubComponentByField(row)}
                <Row className="history-close-box">
                    <Col className="text-right">
                        <Button
                            style={{ marginRight: 15 }}
                            onClick={this.closeSubComponent}
                            variant="secondary"
                            size="lg">
                            Close
                        </Button>
                    </Col>
                </Row>
            </div>
        );
    };

    expandRow = (row: RowInfo<History>) => {
        const expand = this.state.expand && !_.isEmpty(this.state.expand) ? this.state.expand : undefined;
        const expKeys = expand ? Object.keys(expand) : undefined;
        if ( !expKeys || expKeys.indexOf(row.index.toString()) === -1 ) {
            const expand: {[index: number]: boolean} = {};
            expand[row.index] = true;
            this.setState({ expand: expand });
        } else {
            this.setState({ expand: {} });
        }
    };

    render() {
        if (!this.props.historyStore) {
            return null;
        }

        if (this.state.isLoading) {
            return (<div className="is-fetching-history text-center">Loading</div>);
        }

        if (this.state.logs.length === 0) {
            return (<div className="no-history-message">No History</div>);
        }

        return <HistoryReactTable columns={this.columns} data={this.props.historyStore.history} getSubComponentByField={this.getSubComponentByField}/>;
    }
}

interface HistoryReactTableProps {
    columns: ColumnDef<History>[]
    data: History[]
    getSubComponentByField: (row: RowInfo<History>) => JSX.Element | null
}

function HistoryReactTable({columns, data, getSubComponentByField}: HistoryReactTableProps) {
    const table = useReactTable({
        columns,
        data,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getRowCanExpand: () => true,
    });

    const renderSubComponent = (row: RowInfo<History>) => {
        return (
            <div className={"observation-task-form"}>
                <h4>Changes:</h4>
                {getSubComponentByField(row)}
                <Row className="history-close-box">
                    <Col className="text-end">
                        <Button
                            style={{ marginRight: 15 }}
                            onClick={row.getToggleExpandedHandler()}
                            variant="secondary"
                            size="lg">
                            Close
                        </Button>
                    </Col>
                </Row>
            </div>
        );
    };

    return (
        <>
            <Table>
                <thead>
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                            <th key={header.id}>
                                {header.isPlaceholder
                                    ? null
                                    : flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                    )}
                            </th>
                        ))}
                    </tr>
                ))}
                </thead>
                <tbody>
                {table.getRowModel().rows.map(row => (
                    <Fragment key={row.id}>
                        <tr key={row.id}>
                            {row.getVisibleCells().map(cell => (
                                <td key={cell.id}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            ))}
                        </tr>
                        {row.getIsExpanded() && (
                            <tr>
                                <td colSpan={row.getVisibleCells().length}>
                                    {renderSubComponent(row)}
                                </td>
                            </tr>
                        )}
                    </Fragment>
                ))}
                </tbody>
            </Table>
            <div className={"pagination-bottom"}>
                <div className={"pagination"}>
                    <div className={"previous d-grid gap-2"}>
                        <Button disabled={!table.getCanPreviousPage()} onClick={() => table.previousPage()}>Previous</Button>
                    </div>
                    <div className={"center"}>
                        <span className={"page-info"}>
                            Page
                            <div className={"page-jump"}>
                                <Form.Control
                                    type="number"
                                    value={table.getState().pagination.pageIndex + 1}
                                    onChange={e => {
                                        const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                        const pageIndex = table.getState().pagination.pageIndex;
                                        if (page > pageIndex) {
                                            if (table.getCanNextPage()) {
                                                table.setPageIndex(page);
                                            }
                                        } else {
                                            if (table.getCanPreviousPage()) {
                                                table.setPageIndex(page);
                                            }
                                        }
                                    }}/>
                            </div>
                            of {" "}
                            <span className={"total-pages"}>{table.getPageCount()}</span>
                        </span>
                    </div>
                    <div className={"next d-grid gap-2"}>
                        <Button disabled={!table.getCanNextPage()} onClick={() => table.nextPage()}>Next</Button>
                    </div>
                </div>
            </div>
        </>
    );
}